import { utcDayStart } from "./monthMatrix";

/**
 * The calendar day panel's pure seam (ticket #214): what a tapped day
 * shows below the grid. A day's due items — already fetched for the
 * grid by items:monthWindow — bucketed to the selected day and grouped
 * by list, each group's list name the tap target into that list's
 * detail screen. Items are data only: the panel renders them read-only
 * (no tick, no edit affordances — the list detail screen owns editing).
 *
 * Today's panel is the special composition: the cross-list Overdue
 * group (items:overdueWindow's response — incomplete items due before
 * today, oldest first, server-capped) rides ABOVE the due-today list
 * groups. The overdue group is structurally today-only: buildDayPanel
 * nulls it for any other day, so a non-today panel can never leak
 * overdue rows no matter what the caller hands in.
 *
 * All day arithmetic is UTC, matching the grid (monthMatrix) and the
 * stored dueAt convention.
 */

export type PanelItem = {
  itemId: string;
  title: string;
  dueAt: number;
  listId: string;
  listName: string;
};

/** One list's slice of the selected day — the panel's navigable group. */
export type PanelGroup = {
  listId: string;
  listName: string;
  items: PanelItem[];
};

/** The Overdue group's shape, straight off items:overdueWindow. */
export type OverdueSection = {
  items: PanelItem[];
  overflowCount: number;
};

export type DayPanel = {
  dayMs: number;
  isToday: boolean;
  /** Today only: the cross-list Overdue group; null while it loads. */
  overdue: OverdueSection | null;
  /** The day's own items by list (today's = the due-today groups). */
  groups: PanelGroup[];
};

/**
 * The overdue rows the panel renders, at most. The SERVER caps the
 * query at the same number (OVERDUE_CAP in convex/items.ts — that side
 * is the authority, the +N line is computed there); this render-side
 * mirror means a drifted server can flood the panel with rows but never
 * break the "at most 50, then a +N line" shape.
 */
export const OVERDUE_CAP = 50;

export function buildDayPanel({
  selectedDayMs,
  todayMs,
  rows,
  overdue,
}: {
  selectedDayMs: number;
  todayMs: number;
  rows: PanelItem[];
  overdue?: OverdueSection | null;
}): DayPanel {
  const isToday = selectedDayMs === todayMs;

  // Due-then-title: monthWindow rows arrive due-ascending across lists
  // but ties ride merge order — the panel pins its own deterministic
  // order instead of inheriting the query's.
  const dayRows = rows
    .filter((row) => utcDayStart(row.dueAt) === selectedDayMs)
    .sort(
      (a, b) =>
        a.dueAt - b.dueAt ||
        (a.title < b.title ? -1 : a.title > b.title ? 1 : 0),
    );

  const byList = new Map<string, PanelGroup>();
  for (const row of dayRows) {
    let group = byList.get(row.listId);
    if (group === undefined) {
      group = { listId: row.listId, listName: row.listName, items: [] };
      byList.set(row.listId, group);
    }
    group.items.push(row);
  }
  const groups = [...byList.values()].sort(
    (a, b) =>
      (a.listName < b.listName ? -1 : a.listName > b.listName ? 1 : 0) ||
      (a.listId < b.listId ? -1 : 1),
  );

  // The overdue group sorts its own oldest-first order and re-applies
  // the render cap: the server already does both, but the panel's shape
  // (oldest at top, at most OVERDUE_CAP rows, then "+N more") is this
  // module's contract, not an inherited one.
  const overdueSection =
    isToday && overdue !== undefined && overdue !== null
      ? {
          items: [...overdue.items]
            .sort(
              (a, b) =>
                a.dueAt - b.dueAt ||
                (a.title < b.title ? -1 : a.title > b.title ? 1 : 0),
            )
            .slice(0, OVERDUE_CAP),
          overflowCount: overdue.overflowCount,
        }
      : null;

  return {
    dayMs: selectedDayMs,
    isToday,
    overdue: overdueSection,
    groups,
  };
}

/**
 * The today ring's overdue half: the day grid rings today when it holds
 * due-today items (the monthWindow rings) OR overdue items exist —
 * which the capped query reports as rows, or as a flagged overflow when
 * the cap cut the list. Nothing overdue means no ring, bare disc only.
 * A type predicate on purpose: the screen gates the whole Overdue
 * section on it, and the rows inside need the narrowed type.
 */
export function todayHasOverdue(
  overdue: OverdueSection | null | undefined,
): overdue is OverdueSection {
  return (
    overdue !== null &&
    overdue !== undefined &&
    (overdue.items.length > 0 || overdue.overflowCount > 0)
  );
}
